import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {
  Box,
  VStack,
  useTheme,
  Spinner,
  Center,
  HStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalHeader,
  ModalCloseButton,
  Text,
  Image,
  IconButton,
  AvatarGroup,
} from '@chakra-ui/react';
import {motion, AnimatePresence} from 'framer-motion';
import {useSearchParams, useNavigate} from 'react-router-dom';
import ConfettiExplosion from 'react-confetti-explosion';
import styled from 'styled-components';
import MeetUpCard from '../../components/Articles/MeetUpCard';
import EventCard from '../../components/Articles/EventCard';
import OfferCard from '../../components/Articles/Offers/OfferCard';
import ArticleModal from '../../components/Articles/Modal';
import {useQueryClient} from '@tanstack/react-query';
import {TextG12, TextG14, TextG16, TextG24} from '../../components/typography';
import BookmarkButton from '../../components/BookmarkButton';
import {useLogTouchpoint} from '../../hooks/useTouchpointTracking';
import {BlueButton, CustomButton} from '../../components/button';
import Icon from '../../components/Icon';
import UserAvatar from '../../components/UserAvatar';
import UserListCard from '../../components/UsersList/UserListCard';
import useOfferModal from '../../hooks/useOfferModal';
import bookmarkHeaderImg from '../../assets/images/bookmarkHeader.png';
import ChallengeBanner from '../../components/ChallengeBanner';
import {useBookmarkFeedState} from '../../hooks/useBookmarkFeedState';
import {useBookmarkSessionTracking} from '../../hooks/useBookmarkSessionTracking';
import {useBookmarkActions} from '../../hooks/useBookmarkActions';
import useAuth from '../../hooks/useAuth';
import BookmarkAheadLocationPicker, {
  DEFAULT_HOME_SELECTION,
  SelectedLocation,
} from '../../components/BookmarkAheadLocationPicker';

// Ambassador topic ID - when this topic is present, show the "Meet a Community Connector" badge
const AMBASSADOR_PRESENT_TOPIC = process.env.NODE_ENV === 'production' ? 336 : 223;

const StyledConfettiLeft = styled(ConfettiExplosion)`
  position: absolute;
  bottom: 0;
  left: 0;
  transform: rotate(45deg);
`;

const StyledConfettiRight = styled(ConfettiExplosion)`
  position: absolute;
  bottom: 0;
  right: 0;
  transform: rotate(-45deg);
`;

// Session storage key for tracking initial bookmarks
// Key is versioned: bumping it invalidates stale entries written by earlier logic that
// produced incorrect initial-bookmark sets (causing double-counting in the banner).
const BOOKMARK_AHEAD_INITIAL_KEY = 'bookmark_ahead_initial_ids_v2';

const BookmarkAhead = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {showOfferModal} = useOfferModal();
  const {isOpen, onOpen, onClose} = useDisclosure();
  const hasLoggedSession = useRef(false);

  // Location selector — resets to Home on every page mount. Only forwarded to
  // the feed hook when fully resolved (gps/custom modes that have lat+lng); pending GPS
  // does not propagate, so the feed stays anchored on the previous selection until the
  // selector commits.
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocation>(
    DEFAULT_HOME_SELECTION,
  );

  const overrideParams =
    selectedLocation.latitude != null && selectedLocation.longitude != null
      ? {latitude: selectedLocation.latitude, longitude: selectedLocation.longitude}
      : undefined;

  // 'override' affects the Hyper-Local empty-state copy + section visibility under home mode.
  const locationSource: 'home' | 'override' = overrideParams ? 'override' : 'home';

  const {user} = useAuth();
  const hasEarlyAccess = !!user?.hasEarlyAccess;

  // Shared bookmark hooks
  const {
    hyperLocal,
    systemPopular,
    generalUpcoming,
    bookmarkedUpcoming,
    bookmarkedIds,
    feedData,
    loading,
    bannerRef,
    bookmarkedUpcomingSectionRef,
    bannerHeight,
    apiBookmarkedUpcomingCount,
    refetching,
  } = useBookmarkFeedState(overrideParams);

  const {initialBookmarkedIds, sessionBookmarkedIds, setSessionBookmarkedIds} =
    useBookmarkSessionTracking(BOOKMARK_AHEAD_INITIAL_KEY, feedData);

  const {handleBookmark} = useBookmarkActions({
    bookmarkedIds,
    initialBookmarkedIds,
    setSessionBookmarkedIds,
  });

  // Single source of truth for the saved-for-the-future banner AND the exit-confirmation gate.
  // `sessionBookmarkedIds` (this-session-only) drives the summary modal aggregation — keep it
  // separate so the summary still answers "what did the user do this session" even when the
  // exit gate is satisfied by pre-existing saves.
  const bookmarkedCount = apiBookmarkedUpcomingCount;

  // Closed experience tracking state
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showUsersListModal, setShowUsersListModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Fly-to-Save animation: each entry renders a small bookmark icon that animates from the
  // tapped (+) button on a card to the "Saved" pill in the header bar.
  const savedPillRef = useRef<HTMLButtonElement>(null);
  const flyKeyRef = useRef(0);
  const [flyingItems, setFlyingItems] = useState<
    Array<{key: number; from: {x: number; y: number}}>
  >([]);

  const triggerFlyToSave = useCallback((origin: {x: number; y: number}) => {
    flyKeyRef.current += 1;
    const key = flyKeyRef.current;
    setFlyingItems((prev) => [...prev, {key, from: origin}]);
  }, []);

  const removeFlyingItem = useCallback((key: number) => {
    setFlyingItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const queryClient = useQueryClient();

  const handleLocationChange = useCallback((next: SelectedLocation) => {
    setSelectedLocation(next);
  }, []);

  const isFirstLocationRender = useRef(true);
  const pendingLocationScroll = useRef(false);
  const hyperLocalSectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isFirstLocationRender.current) {
      isFirstLocationRender.current = false;
      return;
    }
    pendingLocationScroll.current = true;
    queryClient.invalidateQueries(['bookmark-ahead-feed']);
  }, [selectedLocation.latitude, selectedLocation.longitude, queryClient]);

  // After a location-driven refetch settles, scroll the Hyper-Local section to the top so
  // the user sees their result without needing to scroll up.
  useEffect(() => {
    if (refetching || !pendingLocationScroll.current) return;
    pendingLocationScroll.current = false;
    hyperLocalSectionRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
  }, [refetching]);

  // Touchpoint logging for bookmark ahead session
  const logTouchpointMutation = useLogTouchpoint();

  // Log bookmark_ahead_session completion when user views the feed
  useEffect(() => {
    if (feedData?.data?.data && !hasLoggedSession.current) {
      hasLoggedSession.current = true;
      logTouchpointMutation.mutate({
        touchpoint: 'bookmark_ahead_session',
        action: 'complete',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedData]);

  // Show the "Save at least 3 things" exit popup only when the user's TOTAL future-bookmark count
  // is below the threshold. A user who already has 3+ saved-for-later items (added in a prior
  // session or this one) skips the popup entirely — the goal of the prompt is to ensure the user
  // ends up with a viable saved set, not to force activity in every session.
  const needsExitConfirmation = useCallback(() => {
    return bookmarkedCount < 3;
  }, [bookmarkedCount]);

  // Get session-bookmarked articles for fetching interested users
  const sessionBookmarkedArticles = useMemo(() => {
    const allArticles = [...hyperLocal, ...systemPopular, ...generalUpcoming];
    return allArticles.filter((article) => sessionBookmarkedIds.has(article.id.toString()));
  }, [hyperLocal, systemPopular, generalUpcoming, sessionBookmarkedIds]);

  // State to hold aggregated users from cache when modal opens
  const [aggregatedUsers, setAggregatedUsers] = useState<any[]>([]);
  // State to hold count of offers from bookmarked events
  const [aggregatedOffersCount, setAggregatedOffersCount] = useState(0);

  // When summary modal opens, read users and offers from query cache
  useEffect(() => {
    if (showSummaryModal && sessionBookmarkedArticles.length > 0) {
      const uniqueUsers = new Map<number, any>();
      let totalOffers = 0;

      sessionBookmarkedArticles.forEach((article) => {
        const isOffer = article.type === 'offer';
        const isMeetup = article.type === 'meetup' || article.is_meetup;
        const isEvent = article.type === 'event' || isMeetup;

        // Get users from cache
        const usersQueryKey = isOffer
          ? ['offerBookmarkedUsers', article.id]
          : ['eventInterestedGoingUsers', article.id];

        // Get cached data from the infinite query that cards use
        const cachedData = queryClient.getQueryData<any>(usersQueryKey);

        // Handle infinite query format (pages[0].data.data)
        const users = cachedData?.pages?.[0]?.data?.data || [];

        users.forEach((user: any) => {
          const userId = user?.user_id || user?.id;
          if (userId && !uniqueUsers.has(userId)) {
            // Include article info for chat routing (only for non-offers)
            uniqueUsers.set(userId, {
              ...user,
              // Track if this user can be mentioned in chat (not from an offer)
              canMentionInChat: !isOffer,
              // Store chat route for events/meetups
              chatRoute: !isOffer ? `/${isMeetup ? 'meet-up' : 'event'}/${article.id}/chat` : null,
              articleId: article.id,
            });
          }
        });

        // Count offers from event/meetup from cache
        if (isEvent) {
          const offersQueryKey = [
            'offers',
            {
              page: '1',
              articleType: 'eventId',
              articleId: article.id.toString(),
            },
          ];
          const cachedOffers = queryClient.getQueryData<any>(offersQueryKey);
          const offers = cachedOffers?.data?.data || [];
          totalOffers += offers.length;
        }
      });
      ``;
      setAggregatedUsers(Array.from(uniqueUsers.values()));
      setAggregatedOffersCount(totalOffers);
    }
  }, [showSummaryModal, sessionBookmarkedArticles, queryClient]);

  // Calculate summary data for the completion modal
  const summaryData = useMemo(() => {
    // Calculate total interested count by summing up counts from the articles themselves
    const totalInterestedCount = sessionBookmarkedArticles.reduce((sum, article) => {
      return sum + (article.interested_going_count || article.bookmarks_count || 0);
    }, 0);

    return {
      activitiesCount: sessionBookmarkedArticles.length,
      neighborsCount: totalInterestedCount,
      offersCount: aggregatedOffersCount,
      topUsers: aggregatedUsers.slice(0, 3),
      allUsers: aggregatedUsers,
      additionalCount: Math.max(0, aggregatedUsers.length - 3),
    };
  }, [sessionBookmarkedArticles, aggregatedUsers, aggregatedOffersCount]);

  // Show success toast on exit
  const showExitSuccessToast = useCallback(() => {
    // ToastMessage({
    //   status: 'success',
    //   showStatusIcon: true,
    //   text: 'Great job bookmarking ahead! Keep an eye on your chats for invitations from neighbors with shared interests.',
    //   duration: 5000,
    // });
  }, []);

  // Confirm exit from modal
  const handleConfirmExit = useCallback(() => {
    setShowExitConfirmation(false);
    showExitSuccessToast();
    if (pendingNavigation) {
      pendingNavigation();
    } else {
      navigate('/home');
    }
  }, [pendingNavigation, navigate, showExitSuccessToast]);

  // Keep going from modal
  const handleKeepGoing = useCallback(() => {
    setShowExitConfirmation(false);
    setPendingNavigation(null);
  }, []);

  // Handle close button click
  const handleCloseClick = useCallback(() => {
    if (needsExitConfirmation()) {
      setPendingNavigation(() => () => navigate('/home'));
      setShowExitConfirmation(true);
    } else {
      showExitSuccessToast();
      navigate('/home');
    }
  }, [needsExitConfirmation, showExitSuccessToast, navigate]);

  useEffect(() => {
    if (searchParams.has('id')) {
      onOpen();
    }
  }, [searchParams.get('id'), onOpen]);

  const renderCard = (article: any, section: string) => {
    const isBookmarked = bookmarkedIds.has(article.id.toString());

    // Determine card component based on type field from API
    let CardComponent;
    let cardProps;
    let articleType: 'event' | 'offer' | 'meetup' = 'event';

    if (article.type === 'offer') {
      CardComponent = OfferCard;
      // Suppress the card's internal bookmark element; we overlay the page's own BookmarkButton
      cardProps = {offer: article, showBookmark: false};
      articleType = 'offer';
    } else if (article.type === 'meetup' || article.is_meetup) {
      CardComponent = MeetUpCard;
      cardProps = {'meet-up': article};
      articleType = 'meetup';
    } else {
      CardComponent = EventCard;
      cardProps = {event: article};
      articleType = 'event';
    }

    const handleCardClick = (e?: any) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      // For offers, use the offer modal instead of URL params
      if (article.type === 'offer') {
        showOfferModal({offer: article});
        return;
      }

      const params = new URLSearchParams(searchParams);
      params.set('id', article.id.toString());
      params.set(
        'articleType',
        article.type === 'meetup' || article.is_meetup ? 'meet-up' : article.type,
      );
      setSearchParams(params, {replace: true});
    };

    // Check if this article has the ambassador topic
    const hasAmbassadorTopic = article?.topics_of_interest?.some(
      (topic: any) => topic.id === AMBASSADOR_PRESENT_TOPIC,
    );

    return (
      <Box key={`${section}-${article.id}`} position="relative">
        <CardComponent
          {...cardProps}
          onClick={handleCardClick}
          forcePremium
          hideInterestToggle={true}
        />
        <BookmarkButton
          isBookmarked={isBookmarked}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Fly-to-save: only on add (not remove). Capture the (+) button's center now,
            // before React rerenders, so the animation starts from where the user tapped.
            if (!isBookmarked) {
              const target = e.currentTarget as HTMLElement | null;
              const rect = target?.getBoundingClientRect();
              if (rect) {
                triggerFlyToSave({
                  x: rect.left + rect.width / 2,
                  y: rect.top + rect.height / 2,
                });
              }
            }
            handleBookmark(article);
          }}
          position="absolute"
          top={hasAmbassadorTopic ? '46px' : '12px'}
          right="12px"
          size="36px"
          iconSize="14px"
          // OfferCard wraps its output in a z-index:10 container; Bump above 10 so the overlay stays tappable.
          zIndex={20}
        />
      </Box>
    );
  };

  const loadType = searchParams.has('articleType')
    ? searchParams.get('articleType')?.toLowerCase()
    : 'event';

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      flexDirection="column"
      bg="white">
      {/* Exit Confirmation Modal */}
      <Modal isOpen={showExitConfirmation} onClose={handleKeepGoing} isCentered>
        <ModalOverlay />
        <ModalContent w="90%" maxW="90vw" borderRadius="12px" p={0}>
          <ModalBody p={6} textAlign="center">
            <VStack spacing={4}>
              <Text fontSize="40px">🔖</Text>
              <TextG24 fontSize="26px" fontWeight="600" color="#262626">
                Save at least 3 things
              </TextG24>
              <TextG16 color="#262626" lineHeight="1.5">
                You will always want{' '}
                <Text as="span" fontWeight="700">
                  at least 3 things
                </Text>{' '}
                saved in the future so Walkabout can pair you with interested neighbors.
              </TextG16>
              <CustomButton
                background="#C9136B"
                color="white"
                w="100%"
                h="44px"
                borderRadius="100px"
                boxShadow="0 -4px 0 0 rgba(0, 0, 0, 0.25) inset"
                onClick={handleKeepGoing}
                mt={2}>
                <TextG14 fontWeight="600" color="white">
                  Keep Saving
                </TextG14>
              </CustomButton>
              <Text
                fontSize="14px"
                color="#757D84"
                cursor="pointer"
                onClick={handleConfirmExit}
                textDecoration="underline">
                Skip For Now
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Article Detail Modal */}
      {searchParams.has('id') && (
        <ArticleModal
          open={isOpen}
          close={() => {
            searchParams.delete('id');
            searchParams.delete('articleType');
            setSearchParams(searchParams, {replace: true});
            onClose();
          }}
          type={loadType ?? 'event'}
        />
      )}

      {/* Main Content */}
      <Box
        ref={scrollContainerRef}
        flex={1}
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          scrollbarWidth: 'none',
        }}>
        {/* Header Image with Close Button */}
        <Box position="relative" w="100%">
          {/* <Image src={bookmarkHeaderImg} alt="Bookmark Ahead" w="100%" h="auto" objectFit="cover" /> */}
          <IconButton
            aria-label="Close"
            icon={<Icon iconName="fi fi-rr-cross" style={{fontSize: '12px', color: '#262626'}} />}
            position="absolute"
            top="32px"
            right="18px"
            w="48px"
            h="48px"
            minW="48px"
            bg="white"
            borderRadius="full"
            border="1px solid #E9E9E9"
            boxShadow="md"
            onClick={handleCloseClick}
          />
        </Box>

        {/* Title */}
        <Box px={4} pt={4} pb={2} pr="66px">
          <TextG24 as="span" fontSize="26px" fontWeight="700" lineHeight="34px" color="#C9136B">
            Save activities you might like.{' '}
          </TextG24>
          <TextG24 as="span" fontSize="26px" lineHeight="34px" color="#262626" fontWeight="700">
            See who's planning to go.
          </TextG24>
        </Box>

        {/* Challenge Banner */}
        <ChallengeBanner />

        {/* Consolidated header bar: Location pill (left) + Saved pill (right). Sticky so it
            stays anchored above the section headers as the user scrolls. */}
        <Box
          ref={bannerRef}
          position="sticky"
          top={0}
          zIndex={101}
          bg="white"
          borderBottom="1px solid #E9E9E9"
          px={4}
          py={3}>
          <HStack align="center" spacing={2}>
            {hasEarlyAccess ? (
              <BookmarkAheadLocationPicker
                selected={selectedLocation}
                onChange={handleLocationChange}
              />
            ) : (
              <Box />
            )}
            {bookmarkedCount > 0 && (
              <HStack
                ref={savedPillRef as any}
                as="button"
                onClick={() => {
                  bookmarkedUpcomingSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  });
                }}
                spacing={2}
                align="center"
                bg="white"
                border="1px solid #C9136B"
                borderRadius="100px"
                px={4}
                h="36px"
                flexShrink={0}>
                <Icon
                  iconName="fi fi-sr-bookmark"
                  style={{fontSize: '14px', color: '#C9136B', lineHeight: 1}}
                />
                <TextG14 mt={0} lineHeight="1" fontWeight="700" color="#C9136B">
                  {`${bookmarkedCount} Saved`}
                </TextG14>
              </HStack>
            )}
          </HStack>
        </Box>

        {/* Cards Feed */}
        {loading ? (
          <Center py={8}>
            <Spinner size="lg" color="#C9136B" />
          </Center>
        ) : (
          <VStack spacing={0} align="stretch" px={4} pb="120px">
            {systemPopular.length > 0 && (
              <Box>
                <Box
                  position="sticky"
                  top={`${bannerHeight}px`}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  my={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    🔥 Trending
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Popular with your neighbors
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch">
                  {systemPopular.map((article) => renderCard(article, 'systemPopular'))}
                </VStack>
              </Box>
            )}

            {(hyperLocal.length > 0 || locationSource === 'override' || hasEarlyAccess) && (
              <Box ref={hyperLocalSectionRef} mt={systemPopular.length > 0 ? 6 : 0}>
                <Box
                  position="sticky"
                  top={`${bannerHeight}px`}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  my={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 mb={2} fontWeight="700" color="#262626">
                    📍 Hyper-Local
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    {locationSource === 'override'
                      ? `Close to ${selectedLocation.label}`
                      : 'Close to home'}
                  </TextG12>
                </Box>

                {refetching && !loading ? (
                  <Center py={6}>
                    <Spinner size="sm" color="#C9136B" />
                  </Center>
                ) : hyperLocal.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    {hyperLocal.map((article) => renderCard(article, 'hyperLocal'))}
                  </VStack>
                ) : (
                  <Center py={6}>
                    <TextG14 color={theme.colors.grey} textAlign="center">
                      {'Nothing nearby right now — try a different location above.'}
                    </TextG14>
                  </Center>
                )}
              </Box>
            )}

            {generalUpcoming.length > 0 && (
              <Box mt={hyperLocal.length > 0 || systemPopular.length > 0 ? 6 : 0}>
                <Box
                  position="sticky"
                  top={`${bannerHeight}px`}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  my={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    📅 Coming Up
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Events and offers
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch">
                  {generalUpcoming.map((article) => renderCard(article, 'generalUpcoming'))}
                </VStack>
              </Box>
            )}

            {bookmarkedUpcoming.length > 0 && (
              <Box
                ref={bookmarkedUpcomingSectionRef}
                mt={
                  hyperLocal.length > 0 ||
                  systemPopular.length > 0 ||
                  generalUpcoming.length > 0
                    ? 6
                    : 0
                }>
                <Box
                  position="sticky"
                  top={`${bannerHeight}px`}
                  bg="white"
                  zIndex={100}
                  py={3}
                  px={4}
                  my={4}
                  mx={-4}
                  borderBottom="1px solid"
                  borderColor="gray.200">
                  <TextG16 fontWeight="700" color="#262626">
                    🔖 Saved for the future
                  </TextG16>
                  <TextG12 color={theme.colors.grey} mt={1}>
                    Things you've saved
                  </TextG12>
                </Box>
                <VStack spacing={4} align="stretch">
                  {bookmarkedUpcoming.map((article) => renderCard(article, 'bookmarkedUpcoming'))}
                </VStack>
              </Box>
            )}

            {hyperLocal.length === 0 &&
              systemPopular.length === 0 &&
              generalUpcoming.length === 0 &&
              bookmarkedUpcoming.length === 0 && (
                <Center py={8}>
                  <TextG16 color={theme.colors.gray[600]}>No events available</TextG16>
                </Center>
              )}
          </VStack>
        )}
      </Box>

      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        pointerEvents="none"
        zIndex={250}>
        <AnimatePresence>
          {flyingItems.map((item) => {
            const target = savedPillRef.current?.getBoundingClientRect();
            if (!target) return null;
            const toX = target.left + target.width / 2;
            const toY = target.top + target.height / 2;
            return (
              <motion.div
                key={item.key}
                initial={{
                  position: 'absolute',
                  left: item.from.x - 18,
                  top: item.from.y - 18,
                  opacity: 1,
                  scale: 1,
                }}
                animate={{
                  left: toX - 12,
                  top: toY - 12,
                  opacity: 0.9,
                  scale: 0.6,
                }}
                exit={{opacity: 0}}
                transition={{duration: 0.6, ease: [0.22, 1, 0.36, 1]}}
                onAnimationComplete={() => removeFlyingItem(item.key)}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#C9136B',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 4px 12px rgba(201, 19, 107, 0.35)',
                }}>
                <Icon
                  iconName="fi fi-sr-bookmark"
                  style={{fontSize: '14px', color: 'white', lineHeight: 1}}
                />
              </motion.div>
            );
          })}
        </AnimatePresence>
      </Box>
      <AnimatePresence>
        {sessionBookmarkedIds.size > 0 && !showSummaryModal && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.3}}
            style={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 200,
            }}>
            <Box
              bg="white"
              borderTop="1px solid"
              borderColor="gray.200"
              px={4}
              py={4}
              boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)">
              <BlueButton
                w="100%"
                h="50px"
                background="#C9136B"
                borderRadius="100px"
                boxShadow="0 4px 0 0 rgba(0, 0, 0, 0.25)"
                onClick={() => {
                  if (needsExitConfirmation()) {
                    setShowExitConfirmation(true);
                  } else {
                    setShowSummaryModal(true);
                  }
                }}>
                <TextG16 color="white" fontWeight="600">
                  I'm Done!
                </TextG16>
              </BlueButton>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Modal */}
      <Modal isOpen={showSummaryModal} onClose={() => setShowSummaryModal(false)} isCentered>
        <ModalOverlay bg="rgba(0, 0, 0, 0.6)" />
        <ModalContent w="90%" maxW="90vw" borderRadius="16px" p={0} overflow="hidden">
          <ModalBody p={6} textAlign="center" position="relative">
            {showSummaryModal && (
              <>
                <StyledConfettiLeft
                  zIndex={1500}
                  force={0.9}
                  duration={4000}
                  particleCount={150}
                  width={700}
                  colors={[theme.colors.rose, theme.colors.blue]}
                />
                <StyledConfettiRight
                  zIndex={1500}
                  force={0.9}
                  duration={4000}
                  particleCount={150}
                  width={700}
                  colors={[theme.colors.rose, theme.colors.blue]}
                />
              </>
            )}
            <VStack spacing={4}>
              {/* Party Bells Icon */}
                  <Icon
                    iconName="fi fi-sr-party-bell"
                    style={{
                      fontSize: '40px',
                      background: 'linear-gradient(135deg, #E8A54B 0%, #C9136B 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  />

                  {/* Title */}
                  <VStack spacing={0}>
                    <TextG24 fontSize="24px" fontWeight="700" color="#262626">
                      You're all set! Plans are
                    </TextG24>
                    <TextG24 fontSize="24px" fontWeight="700" color="#262626">
                      coming together...
                    </TextG24>
                  </VStack>

                  {/* Stats with icons */}
                  <VStack spacing={3} align="stretch" w="100%" mt={2}>
                    {/* Neighbors stat - only show if any neighbors are interested */}
                    {summaryData.neighborsCount > 0 && (
                      <HStack spacing={3} align="flex-start">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="8px"
                          bg="rgba(201, 19, 107, 0.1)"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}>
                          <Icon
                            iconName="fi fi-sr-users"
                            style={{fontSize: '18px', color: '#C9136B'}}
                          />
                        </Box>
                        <Text fontSize="15px" color="#262626" textAlign="left">
                          <Text as="span" fontWeight="700" fontSize="18px">
                            {summaryData.neighborsCount}
                          </Text>{' '}
                          neighbors are interested in the same things you just saved
                        </Text>
                      </HStack>
                    )}

                    {/* Offers stat - only show if there are offers */}
                    {summaryData.offersCount > 0 && (
                      <HStack spacing={3} align="flex-start">
                        <Box
                          w="40px"
                          h="40px"
                          borderRadius="8px"
                          bg="rgba(201, 19, 107, 0.1)"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}>
                          <Icon
                            iconName="fi fi-sr-gift"
                            style={{fontSize: '18px', color: '#C9136B'}}
                          />
                        </Box>
                        <Text fontSize="15px" color="#262626" textAlign="left">
                          <Text as="span" fontWeight="700" fontSize="18px">
                            {summaryData.offersCount}
                          </Text>{' '}
                          businesses have special deals and offers for you
                        </Text>
                      </HStack>
                    )}
                  </VStack>

                  {/* Bonus Tip */}
                  <Box position="relative" pt={3} mt="30px !important" w="100%">
                    <Box
                      position="absolute"
                      top="-16px"
                      left="16px"
                      bg="#FAE7F0"
                      borderTopRadius="8px"
                      px="12px"
                      py="4px"
                      zIndex={1}>
                      <TextG12 fontWeight="700" color="#C9136B">
                        Bonus Tip
                      </TextG12>
                    </Box>
                    <Box
                      textAlign="left"
                      bg="#F8F6F7"
                      borderRadius="8px"
                      p="16px"
                      border="2px solid #F4CFE2">
                      <TextG14 fontWeight="400" lineHeight="22px" color="#363636">
                        Keep coming back to save what feels interesting, and stay connected with
                        your local community.
                      </TextG14>
                    </Box>
                  </Box>

                  {/* My Stuff link note */}
                  <Box
                    bg="#F0F7FF"
                    borderRadius="12px"
                    p={4}
                    w="100%"
                    mt={2}
                    border="1px solid #D0E4FF">
                    <TextG14 color="#262626" textAlign="center">
                      See all your saves in{' '}
                      <Text
                        as="span"
                        color="#C9136B"
                        fontWeight="700"
                        cursor="pointer"
                        textDecoration="underline"
                        onClick={() => {
                          setShowSummaryModal(false);
                          navigate('/my-stuff?tab=Saves');
                        }}>
                        My Stuff
                      </Text>
                    </TextG14>
                  </Box>

                  {/* Go To Home button */}
                  <CustomButton
                    background="#C9136B"
                    color="white"
                    w="100%"
                    h="48px"
                    borderRadius="100px"
                    boxShadow="0 4px 0 0 rgba(0, 0, 0, 0.25)"
                    onClick={() => {
                      setShowSummaryModal(false);
                      showExitSuccessToast();
                      navigate('/home');
                    }}
                    mt={4}>
                    <TextG14 fontWeight="600" color="white">
                      Go To Home
                    </TextG14>
                  </CustomButton>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Users List Modal */}
      <Modal
        isOpen={showUsersListModal}
        onClose={() => setShowUsersListModal(false)}
        size="md"
        scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxH="80vh">
          <ModalHeader>
            <TextG14 fontWeight="600">Neighbors Interested</TextG14>
            <TextG12 color={theme.colors.grey} fontWeight="400" mt={1}>
              {summaryData.allUsers.length}{' '}
              {summaryData.allUsers.length === 1 ? 'person' : 'people'}
            </TextG12>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4} overflowY="auto">
            {summaryData.allUsers.length === 0 ? (
              <Center py={10}>
                <TextG14 color={theme.colors.grey}>No users yet</TextG14>
              </Center>
            ) : (
              <VStack spacing={2} align="stretch">
                {summaryData.allUsers.map((user: any) => (
                  <UserListCard
                    key={user.id || user.user_id}
                    user={{
                      user_id: user.id || user.user_id,
                      name: user.first_name || user.name || '',
                      surname: user.last_name || user.surname || '',
                      handle: user.handle || '',
                      profile_photo_url: user.avatar?.url || user.profile_photo_url || '',
                      latitude: user.latitude,
                      longitude: user.longitude,
                      created_at: user.created_at || '',
                    }}
                    onMentionClick={
                      user.canMentionInChat && user.chatRoute
                        ? () => {
                            setShowUsersListModal(false);
                            setShowSummaryModal(false);
                            navigate(`${user.chatRoute}?mention=${user.handle || ''}`);
                          }
                        : undefined
                    }
                  />
                ))}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BookmarkAhead;
